import React from 'react';

import { defineMessages, FormattedMessage } from 'react-intl-next';

import Button from '@atlaskit/button/standard-button';
import { getAtlassianContextUrl } from '@atlassian/teams-common/context-config';

import ErrorImageSvg from '../../assets/error-page/svg-error-window_ptc.svg';

import * as Styled from './ErrorPage.styled';

interface Props {
	title: JSX.Element | string;
	body?: JSX.Element | string;
	/** Element that shows up in the image slot, it needs to have its own width and margin-bottom */
	image?: JSX.Element;
	actions?: JSX.Element;
}

export const DefaultErrorImage: React.JSX.Element = (
	<Styled.ErrorImage alt="" src={ErrorImageSvg} />
);

export const ErrorPage = ({ title, body, image, actions }: Props): JSX.Element => {
	return (
		<Styled.Container data-test-selector="error-page">
			<Styled.ImageOuter>{image || DefaultErrorImage}</Styled.ImageOuter>
			<Styled.ErrorTextContainer>
				<Styled.TitleWrapper>
					<Styled.Title>{title}</Styled.Title>
				</Styled.TitleWrapper>
				{body && (
					<Styled.DescriptionWrapper>
						<Styled.Description>{body}</Styled.Description>
					</Styled.DescriptionWrapper>
				)}
			</Styled.ErrorTextContainer>
			{actions && <Styled.ButtonContainer>{actions}</Styled.ButtonContainer>}
		</Styled.Container>
	);
};

export const GenericErrorPage = (props: { hasSupportLink?: boolean }): JSX.Element => {
	const actions = props.hasSupportLink && (
		<Button
			rel="noopener noreferrer"
			target="_blank"
			href={`${getAtlassianContextUrl('support')}/contact/#/`}
		>
			<FormattedMessage {...messages.contactUs} />
		</Button>
	);

	return (
		<ErrorPage
			title={<FormattedMessage {...messages.title} />}
			body={<FormattedMessage {...messages.description} />}
			actions={actions || undefined}
		/>
	);
};

const messages = defineMessages({
	title: {
		id: 'pf-home.generic-error-title',
		defaultMessage: 'Something went wrong',
	},
	contactUs: {
		id: 'ptc-directory.error-boundary.message-link-text',
		defaultMessage: 'Contact support',
		description:
			'The text of the link leading to the Atlassian support website, shown below the error description on the error pages',
	},
	description: {
		id: 'pf-home.generic-error-description',
		defaultMessage:
			'We keep track of these errors, but feel free to contact us if refreshing doesn’t fix things.',
	},
});

export default ErrorPage;
